import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import AboutSubNav from "@/components/feature/AboutSubNav";
import type { ProductItem } from "@/lib/types";

interface OverviewRow {
  label: string;
  value: string;
}

interface Facility {
  name: string;
  spec: string;
  count: string;
}

interface CoreValue {
  label: string;
  desc: string;
}

interface CompanyOverview {
  name: string;
  enName: string;
  established: string;
  ceo: string;
  businessType: string;
  companyInfoTitle: string;
  facilityTitle: string;
  productsTitle: string;
  coreValuesTitle: string;
  visionLabel: string;
  missionLabel: string;
  rows: OverviewRow[];
  mainProducts: string[];
  facilities: Facility[];
  vision: string;
  mission: string;
  coreValues: CoreValue[];
  detail: string;
}

export default function AboutOverviewPage() {
  const { t } = useTranslation();
  const companyOverview = t("about.overview", { returnObjects: true }) as unknown as CompanyOverview;
  const allProducts = t("products.items", { returnObjects: true }) as unknown as ProductItem[];

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title={t("about.banners.overview.title")}
        subtitle={t("about.banners.overview.subtitle")}
        breadcrumb={[
          { label: t("about.companyLabel"), to: "/about/overview" },
          { label: t("about.banners.overview.title") },
        ]}
        bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20manufacturing%20facility%20interior%20with%20organized%20production%20floor%20and%20natural%20light%20streaming%20through%20windows%2C%20professional%20engineering%20environment%2C%20warm%20industrial%20aesthetic%2C%20soft%20ambient%20lighting%2C%20crisp%20architectural%20photography&width=1600&height=680&seq=krubber-about-banner&orientation=landscape"
      />
      <AboutSubNav />

      <section className="py-16 md:py-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                {companyOverview.companyInfoTitle}
              </h2>
              <div className="mt-8 border border-background-200/70 rounded-xl overflow-hidden">
                {companyOverview.rows.map((row, i) => (
                  <div
                    key={row.label}
                    className={`flex items-center px-6 py-4 ${
                      i % 2 === 0 ? "bg-background-50" : "bg-background-100"
                    }`}
                  >
                    <span className="w-36 text-sm font-semibold text-foreground-600 flex-shrink-0">
                      {row.label}
                    </span>
                    <span className="text-sm text-foreground-900">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                {companyOverview.facilityTitle}
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-3">
                {companyOverview.facilities.map((f) => (
                  <div
                    key={f.name}
                    className="flex items-center justify-between p-4 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
                        <i className="ri-settings-3-line text-primary-600 text-sm" />
                      </span>
                      <span className="text-sm font-semibold text-foreground-900">{f.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-foreground-600">{f.spec}</span>
                      <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                        {f.count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-16 lg:mt-20">
            <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950 text-center">
              {companyOverview.productsTitle}
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {allProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="group rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200/60 overflow-hidden transition-all cursor-pointer"
                >
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                        {product.category}
                      </span>
                      <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                        {product.subcategory}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {product.name}
                    </h3>
                    <div className="mt-3 flex flex-col gap-1.5">
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-settings-3-line text-[11px]" />
                        {product.application}
                      </span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-drop-line text-[11px]" />
                        {product.material}
                      </span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-background-200/70">
                      <span className="text-xs font-semibold text-primary-600 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        {companyOverview.detail}
                        <i className="ri-arrow-right-line text-[10px]" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-16 lg:mt-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              <div className="p-8 rounded-2xl bg-primary-50 border border-primary-200/60">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold mb-4">
                  {companyOverview.visionLabel}
                </span>
                <p className="text-lg font-heading font-bold text-foreground-950 leading-snug">
                  {companyOverview.vision}
                </p>
              </div>
              <div className="p-8 rounded-2xl bg-accent-50 border border-accent-200/60">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-bold mb-4">
                  {companyOverview.missionLabel}
                </span>
                <p className="text-lg font-heading font-bold text-foreground-950 leading-snug">
                  {companyOverview.mission}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 lg:mt-20">
            <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950 text-center">
              {companyOverview.coreValuesTitle}
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {companyOverview.coreValues.map((v) => (
                <div
                  key={v.label}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
                >
                  <p className="font-heading font-extrabold text-xl text-foreground-950">{v.label}</p>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}